/**
 * Default Layout Generator for Dashboard V2
 * Generates initial layout configuration for new users
 */

import { DashboardLayout, WidgetId, DashboardWidget } from "../types";

export const ALL_WIDGET_IDS: WidgetId[] = [
  "retirement-readiness",
  "aum-by-product",
  "contribution-rate",
  "social-security",
  "retirement-income-sources",
  "fegli-risk",
  "age-distribution",
  "rollover-opportunity",
  "employer-match",
  "location-insights",
  "plan-type-spread",
  "longevity-stress",
];

/**
 * Generates a unique instance ID for a widget
 * Falls back to timestamp-based ID if uuid is not available
 */
export function generateInstanceId(widgetId: WidgetId): string {
  try {
    // Try to use uuid if available
    const { v4: uuidv4 } = require("uuid");
    return uuidv4();
  } catch {
    // Fallback to timestamp-based ID
    return `${widgetId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Generates default layout with all widgets in 2-column grid
 */
export function generateDefaultLayout(): DashboardLayout {
  const widgets: DashboardWidget[] = ALL_WIDGET_IDS.map((widgetId, index) => {
    const row = Math.floor(index / 2); // 2 columns
    const col = index % 2;
    
    return {
      widgetId,
      instanceId: generateInstanceId(widgetId),
      x: col * 6,           // Each widget takes 6 columns (50% width in 12-col grid)
      y: row * 8,            // Each widget takes 8 grid units height
      w: 6,                  // Width: 6 grid units (50% of 12-column grid)
      h: 8,                  // Height: 8 grid units (~400px at 50px/unit)
      minW: 3,               // Minimum: 3 units (25% width)
      minH: 6,               // Minimum: 6 units (~300px height)
      maxW: 12,              // Maximum: 12 units (100% width)
      visible: true,
    };
  });

  return {
    widgets,
    gridColumns: 12,         // Base grid (lg breakpoint)
    gridRowHeight: 50,
    breakpoints: { 
      lg: 1200,              // Large: 4 columns
      md: 996,               // Medium: 3 columns
      sm: 768,               // Small: 2 columns
      xs: 480,               // Extra small: 2 columns
      xxs: 0                 // Extra extra small: 1 column
    },
    cols: { 
      lg: 2,                 // 2 columns on large screens (matches dropdown options: 1, 2, 3)
      md: 2,                 // 2 columns on medium screens
      sm: 2,                 // 2 columns on small screens
      xs: 2,                 // 2 columns on extra small screens
      xxs: 1                 // 1 column on mobile
    },
    isFluid: true,           // Default to fluid layout (full width)
    version: 1,
  };
}

/**
 * Validates layout structure and fixes common issues
 * IMPORTANT: Preserves widget order from the saved layout
 */
export function validateLayout(layout: any): DashboardLayout {
  // If layout is missing or invalid, return default
  if (!layout || !layout.widgets || !Array.isArray(layout.widgets)) {
    console.log("validateLayout: Invalid layout, returning default");
    return generateDefaultLayout();
  }

  // Log the incoming widget order for debugging
  console.log("validateLayout: Input widget order:", layout.widgets.map((w: any) => w.widgetId));

  // CRITICAL: Preserve the EXACT order from the saved layout
  // Map directly over the original widgets array - this maintains order
  // Do NOT use a Map lookup as it could potentially reorder or lose widgets
  const validatedWidgets = layout.widgets
    .filter((w: any) => w && w.widgetId) // Filter out invalid entries
    .map((w: any) => {
      // Directly transform each widget while preserving array order
      // CRITICAL: Preserve visibility state exactly as saved (don't default to true)
      return {
        widgetId: w.widgetId,
        instanceId: w.instanceId || generateInstanceId(w.widgetId),
        x: typeof w.x === 'number' ? w.x : 0,
        y: typeof w.y === 'number' ? w.y : 0,
        w: typeof w.w === 'number' ? w.w : 6,
        h: typeof w.h === 'number' ? w.h : 8,
        minW: w.minW || 3,
        minH: w.minH || 6,
        maxW: w.maxW || 12,
        maxH: w.maxH,
        static: w.static || false,
        // CRITICAL: Preserve visibility exactly as saved (true/false/undefined)
        // Only default to true if the property is completely missing (not saved)
        visible: w.hasOwnProperty('visible') ? w.visible : true,
      };
    });

  // Add any missing widgets from ALL_WIDGET_IDS at the end (preserve order for existing ones)
  const existingWidgetIds = new Set(validatedWidgets.map((w: DashboardWidget) => w.widgetId));
  const missingWidgets: DashboardWidget[] = [];
  
  ALL_WIDGET_IDS.forEach((widgetId) => {
    if (!existingWidgetIds.has(widgetId)) {
      // Calculate position for missing widget
      const maxY = validatedWidgets.length > 0
        ? Math.max(...validatedWidgets.map((w: DashboardWidget) => w.y + w.h), 0)
        : 0;
      const row = Math.floor(validatedWidgets.length / 2); // 2 columns
      const col = validatedWidgets.length % 2;
      
      missingWidgets.push({
        widgetId,
        instanceId: generateInstanceId(widgetId),
        x: col * 6,
        y: row * 8,
        w: 6,
        h: 8,
        minW: 3,
        minH: 6,
        maxW: 12,
        visible: true,
      });
    }
  });

  const finalWidgets = [...validatedWidgets, ...missingWidgets];

  // Log the output widget order for debugging
  console.log("validateLayout: Output widget order:", finalWidgets.map(w => w.widgetId));

  // CRITICAL: Preserve column layout exactly as saved
  // Don't use || operator as it will replace empty objects or falsy values
  const defaultCols = {
    lg: 2,  // Default to 2 columns (matches dropdown options: 1, 2, 3)
    md: 2,
    sm: 2,
    xs: 2,
    xxs: 1,
  };
  
  const defaultBreakpoints = {
    lg: 1200,
    md: 996,
    sm: 768,
    xs: 480,
    xxs: 0,
  };

  // Normalize cols.lg to valid dropdown value (1, 2, or 3)
  // If cols.lg is 4 or any other invalid value, default to 2
  const normalizeColsLg = (lgValue: number): number => {
    if (lgValue === 1 || lgValue === 2 || lgValue === 3) {
      return lgValue;
    }
    // If value is 4 or invalid, default to 2
    return 2;
  };

  const validated: DashboardLayout = {
    widgets: finalWidgets,
    gridColumns: layout.gridColumns || 12,
    gridRowHeight: layout.gridRowHeight || 50,
    // Preserve breakpoints if they exist, otherwise use defaults
    breakpoints: layout.breakpoints && typeof layout.breakpoints === 'object' 
      ? { ...defaultBreakpoints, ...layout.breakpoints }
      : defaultBreakpoints,
    // CRITICAL: Preserve cols exactly as saved - merge with defaults only if missing properties
    // Normalize cols.lg to ensure it matches dropdown options (1, 2, or 3)
    cols: layout.cols && typeof layout.cols === 'object'
      ? { 
          ...defaultCols, 
          ...layout.cols,
          lg: normalizeColsLg(layout.cols.lg || defaultCols.lg) // Normalize lg to valid value
        }
      : defaultCols,
    // CRITICAL: Preserve layout mode (fluid/boxed) exactly as saved
    isFluid: layout.hasOwnProperty('isFluid') ? layout.isFluid : true,
    version: layout.version || 1,
  };
  
  // Log preserved values for debugging
  console.log("validateLayout: Preserved cols:", JSON.stringify(validated.cols));
  console.log("validateLayout: Widget visibility states:", validatedWidgets.map((w: DashboardWidget) => `${w.widgetId}:${w.visible}`).join(', '));

  return validated;
}
